import { FC, useEffect, useMemo, useState } from 'react';
import { BaseRiskProfileRequest } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTranslation } from 'react-i18next';
import { riskscoreProfilesActions } from '@/redux/actions';
import { riskscoreProfilesSelectors } from '@/redux/selectors';
import AnimateHeight, { Height } from 'react-animate-height';
import { Loader } from '@/components/Loader';
import { Button } from '@/components/Button';
import { RiskProfileSignalTable } from '@/components/RiskProfileSignalTable';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

import styles from './RiskscoreProfileDetailsModal.module.sass';

interface RiskscoreProfileDetailsModalProps extends ModalInfoProps {
  profileId: number | null;
  onEdit: (profileId: number) => void;
}

const RiskscoreProfileDetailsModal: FC<RiskscoreProfileDetailsModalProps> = (
  props,
) => {
  const { onClose, isOpen, profileId, onEdit } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loaderHeight, setLoaderHeight] = useState<Height>(0);
  const { data: riskProfileData, profileLoading } = useAppSelector(
    riskscoreProfilesSelectors.getRiskscoreProfilesSlice,
  );

  useEffect(() => {
    if (isOpen && profileId) {
      const payload: BaseRiskProfileRequest = { profileId };
      dispatch(riskscoreProfilesActions.fetchRiskProfile(payload));
    }
  }, [profileId, isOpen]);

  useEffect(() => {
    setLoaderHeight(profileLoading ? 'auto' : 0);
  }, [profileLoading]);

  const currentProfile = useMemo(() => {
    if (riskProfileData) {
      return riskProfileData.profiles.find((item) => item.id === profileId);
    }

    return null;
  }, [profileId, riskProfileData]);

  const onReset = () => {
    if (profileId) {
      const payload: BaseRiskProfileRequest = { profileId };
      dispatch(riskscoreProfilesActions.resetRiskProfile(payload));
    }
  };

  const modalTitle = useMemo(() => {
    if (currentProfile) {
      return t('translation.risk_profile_modal_title', {
        name: currentProfile.name,
      });
    }

    return '';
  }, [t, currentProfile]);

  return (
    <ModalInfo
      isOpen={isOpen}
      title={modalTitle}
      className={styles.modal}
      actions={
        <div className={styles.footerActions}>
          {!currentProfile?.locked && (
            <Button
              type="secondary-red"
              size="s"
              corner="rect"
              disabled={profileLoading}
              className={styles.reset}
              onClick={onReset}
            >
              {t('translation.reset_to_default')}
            </Button>
          )}
          <Button
            type="secondary"
            size="m"
            corner="rect"
            disabled={profileLoading}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <AnimateHeight
        className={styles.loader}
        duration={100}
        height={loaderHeight}
      >
        <Loader />
      </AnimateHeight>

      {currentProfile && (
        <>
          {!!currentProfile.description && (
            <div className={styles.description}>
              {currentProfile.description}
            </div>
          )}
          <RiskProfileSignalTable
            profile={currentProfile}
            loading={profileLoading}
          />
        </>
      )}
    </ModalInfo>
  );
};

export default RiskscoreProfileDetailsModal;
